/**
 * The saved AI reports list's data and handlers, shared by `MemoSummaryList` and the desktop
 * project record's saved reports panel: the logged-in user's reports, the search form (debounced),
 * the dates driven by the notes filters when asked, sorting, the page, the report being viewed and
 * the PDF build state.
 */
import { type AiResponse, AiResponseReport } from '@kalos/kalos-rpc';
import { type SortingState } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';

import { FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME } from '../../../../constants';
import { useAuth } from '../../../../context/AuthContext';
import { useAiResponseQuery } from '../../../../hooks/react-query/useAiResponseQuery';
import { OpenAIClientService } from '../../../../tools/helpers';
import {
  getDefaultMemoSummarySearchFields,
  type MemoSummarySearchSchemeType,
  useMemoSummarySearchForm,
} from './MemoSummaryUtils';

/** Opens the built PDF of a report in a new tab. */
export const downloadPdf = async (aiResponseId: number) => {
  const batch = await OpenAIClientService.BatchGetAiResponseReport?.(
    AiResponseReport.create({ aiResponsesId: aiResponseId }),
  );
  const reportId = batch?.results?.[0]?.id;
  if (!reportId) return;
  const urlObj = await OpenAIClientService.DownloadAiResponseReport?.(
    AiResponseReport.create({ id: reportId }),
  );
  if (urlObj?.url) window.open(urlObj.url, '_blank');
};

export const formatReportDate = (createdAt: string | undefined, pattern: string) => {
  if (!createdAt) return 'N/A';
  try {
    return format(parseISO(createdAt), pattern);
  } catch {
    return createdAt;
  }
};

export interface UseMemoSummaryListArgs {
  /** Phones: told when a saved report opens or closes in place of the list. */
  onReportOpenChange?: (isOpen: boolean) => void;
  /** When true, `startDate` / `endDate` are read-only and driven by `parentDateRange`. */
  datesControlledExternally?: boolean;
  /** Used when `datesControlledExternally` is true (e.g. Job Note filter dates as `yyyy-MM-dd`). */
  parentDateRange?: {
    startDate?: string;
    endDate?: string;
  };
  /** Phones: a report opens in place of the list. */
  mobileLayout?: boolean;
}

export const useMemoSummaryList = ({
  onReportOpenChange,
  datesControlledExternally = false,
  parentDateRange,
  mobileLayout = false,
}: UseMemoSummaryListArgs) => {
  const { user } = useAuth();
  const loggedUserId = user.id;
  const [selectedAiResponse, setSelectedAiResponse] = useState<AiResponse | null>(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [buildPdfAiResponse, setBuildPdfAiResponse] = useState<AiResponse | null>(null);
  const [isBuildPdfModalOpen, setIsBuildPdfModalOpen] = useState(false);
  const [hasBuiltPdf, setHasBuiltPdf] = useState<Record<number, boolean>>({});
  const isViewingReport = mobileLayout && !!selectedAiResponse && isReportDialogOpen;
  useEffect(() => {
    onReportOpenChange?.(isViewingReport);
  }, [isViewingReport, onReportOpenChange]);

  const canBuildPdf = useMemo(
    () => user.permissionGroups.some((p) => p.name === 'DevTesting'),
    [user.permissionGroups],
  );

  const formDefaultValues = useMemo(() => {
    if (!datesControlledExternally || !parentDateRange) {
      return getDefaultMemoSummarySearchFields();
    }
    return getDefaultMemoSummarySearchFields({
      startDate: parentDateRange.startDate,
      endDate: parentDateRange.endDate,
    });
  }, [datesControlledExternally, parentDateRange]);

  const form = useMemoSummarySearchForm({
    defaultValues: formDefaultValues,
  });

  const [filter, setFilter] = useState<MemoSummarySearchSchemeType>(form.getValues());
  const [page, setPage] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'id', desc: true }]);
  const debouncedFilterRef = useRef<ReturnType<typeof debounce> | undefined>(undefined);

  useEffect(() => {
    debouncedFilterRef.current = debounce((data: MemoSummarySearchSchemeType) => {
      setFilter(data);
      setPage(0);
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);

    const subscription = form.watch((formData) => {
      if (debouncedFilterRef.current) {
        debouncedFilterRef.current(formData as MemoSummarySearchSchemeType);
      }
    });

    return () => {
      subscription.unsubscribe();
      debouncedFilterRef.current?.cancel();
    };
  }, [form]);

  useEffect(() => {
    if (!datesControlledExternally || !parentDateRange) return;
    form.setValue('startDate', parentDateRange.startDate, { shouldDirty: false });
    form.setValue('endDate', parentDateRange.endDate, { shouldDirty: false });
  }, [datesControlledExternally, form, parentDateRange]);

  // Reset page when sorting changes
  useEffect(() => {
    setPage(0);
  }, [sorting]);

  const queryFilters = useMemo(() => {
    const filters: Record<string, unknown> = {
      createdByUserId: loggedUserId,
      isActive: true,
      pageNumber: page,
    };

    if (filter.startDate && filter.endDate) {
      filters.dateRange = ['>=', filter.startDate, '<', filter.endDate];
      filters.dateTarget = ['created_at', 'created_at'];
    } else if (filter.startDate) {
      filters.dateRange = ['>=', filter.startDate];
      filters.dateTarget = ['created_at'];
    } else if (filter.endDate) {
      filters.dateRange = ['<', filter.endDate];
      filters.dateTarget = ['created_at'];
    }

    if (filter.response) {
      filters.response = `%${filter.response}%`;
    }

    // Add sorting
    if (sorting.length > 0) {
      const sort = sorting[0];
      filters.orderBy = sort.id;
      filters.orderDir = sort.desc ? 'DESC' : 'ASC';
    }

    return filters;
  }, [loggedUserId, filter, page, sorting]);

  const aiResponseQuery = useAiResponseQuery({
    filters: queryFilters,
    enabled: loggedUserId !== 0,
  });

  const totalCount = aiResponseQuery.data?.totalCount ?? 0;
  const results = aiResponseQuery.data?.results ?? [];

  return {
    loggedUserId,
    selectedAiResponse,
    setSelectedAiResponse,
    isReportDialogOpen,
    setIsReportDialogOpen,
    buildPdfAiResponse,
    setBuildPdfAiResponse,
    isBuildPdfModalOpen,
    setIsBuildPdfModalOpen,
    hasBuiltPdf,
    setHasBuiltPdf,
    canBuildPdf,
    form,
    page,
    setPage,
    sorting,
    setSorting,
    queryFilters,
    aiResponseQuery,
    totalCount,
    results,
  };
};
